import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Send, ChevronDown, ChevronRight, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import EmailPreviewConfirm from '../../components/email/EmailPreviewConfirm';

const DEFAULT_SUBJECT = 'Ihre Beitragsaufstellung – <<Schulname>>';
const DEFAULT_BODY = `<<Briefanrede>>,

anbei erhalten Sie die aktuelle Aufstellung Ihrer Monatsbeiträge (Stand: <<Datum>>) als PDF.

Bei Fragen zu den Beiträgen oder Rabatten melden Sie sich gerne bei uns.

Mit freundlichen Grüßen
<<Schulname>>`;

function plainToPreviewHtml(text) {
  const esc = (s) => String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const paras = esc(text).split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
  return `<html><body style="font-family:sans-serif;color:#0f172a">${paras}</body></html>`;
}

export function BulkPreviewSendDialog({ filteredIds }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('all');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [sending, setSending] = useState(false);
  const [mailPreview, setMailPreview] = useState(null);
  const bodyRef = useRef(null);

  const loadPreview = useCallback(async (sc) => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const r = await api.post('/guardians/billing-preview-bulk-email', {
        dry_run: true,
        scope: sc,
        guardian_ids: sc === 'selection' ? filteredIds : undefined,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vorschau konnte nicht geladen werden');
    } finally { setPreviewLoading(false); }
  }, [filteredIds]);

  useEffect(() => {
    if (!open) return;
    loadPreview(scope);
  }, [open, scope, loadPreview]);

  useEffect(() => {
    if (!open) return;
    api.get('/correspondence/templates').then((r) => setTemplates(r.data)).catch(() => {});
    api.get('/correspondence/placeholders').then((r) => setPlaceholders(r.data)).catch(() => {});
  }, [open]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      if (t.subject) setSubject(t.subject);
      if (t.body) setBody(t.body);
    }
  };

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + key); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + key + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + key.length;
    });
  };

  const openContentPreview = () => {
    if (!preview?.eligible) return;
    setMailPreview({
      subject: subject || DEFAULT_SUBJECT,
      html: plainToPreviewHtml(body || DEFAULT_BODY),
      eligible: preview.eligible,
      recipients: preview.recipients || [],
    });
  };

  const send = async (routing = {}) => {
    if (!preview?.eligible) return;
    setSending(true);
    try {
      const r = await api.post('/guardians/billing-preview-bulk-email', {
        scope,
        guardian_ids: scope === 'selection' ? filteredIds : undefined,
        subject,
        body,
        template_id: templateId || undefined,
        preview_confirmed: true,
        ...routing,
      });
      toast.success(r.data.message || `Sammel-Versand für ${r.data.count} Zahler gestartet`);
      setMailPreview(null);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sammel-Versand fehlgeschlagen');
    } finally { setSending(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="bulk-preview-send-btn">
        <Send className="h-4 w-4 mr-1.5" /> Sammel-Versand
      </Button>
      <Dialog open={open && !mailPreview} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-preview-send-dialog">
          <DialogHeader>
            <DialogTitle>Sammel-Versand Beitragsaufstellung</DialogTitle>
            <DialogDescription>
              Verschickt die aktuelle Beitragsaufstellung als PDF an die Zahlungspflichtigen des Mandanten.
              Die E-Mails landen in der Freigabe-Queue.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => setScope('all')}
                className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'all' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                data-testid="bulk-send-scope-all"
              >
                Alle aktiven Zahler
              </button>
              <button
                onClick={() => setScope('selection')}
                className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'selection' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                data-testid="bulk-send-scope-selection"
              >
                Aktuell gefilterte Liste ({filteredIds.length})
              </button>
            </div>

            {previewLoading ? (
              <div className="py-4 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
            ) : preview && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="bulk-send-preview-stats">
                <div className="font-medium text-slate-900">
                  {preview.eligible === 1 ? '1 E-Mail wird erstellt' : `${preview.eligible} E-Mails werden erstellt`}
                </div>
                {preview.skipped?.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowSkipped((v) => !v)}
                      className="flex items-center gap-1 text-xs text-amber-700 font-medium"
                      data-testid="bulk-send-skipped-toggle"
                    >
                      {showSkipped ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {preview.skipped.length} Familie{preview.skipped.length === 1 ? ' wird' : 'n werden'} übersprungen
                    </button>
                    {showSkipped && (
                      <div className="mt-2 max-h-40 overflow-y-auto divide-y divide-slate-200" data-testid="bulk-send-skipped-list">
                        {preview.skipped.map((s) => (
                          <div key={s.id} className="flex justify-between gap-3 py-1 text-xs">
                            <span className="text-slate-700">{s.name}</span>
                            <span className="text-slate-500 shrink-0">{s.reason}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-slate-500">Textvorlage (Korrespondenz)</label>
              <Select value={templateId} onValueChange={applyTemplate}>
                <SelectTrigger className="mt-1" data-testid="bulk-send-template-select">
                  <SelectValue placeholder="Vorlage wählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.is_favorite ? '★ ' : ''}{t.module} · {t.name}</SelectItem>
                  ))}
                  {templates.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Keine Vorlagen – im Korrespondenzmodul anlegen</div>}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">Betreff</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="bulk-send-subject" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">Text</label>
              <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={9} className="mt-1 font-mono text-xs" data-testid="bulk-send-body" />
              <div className="flex flex-wrap gap-1 mt-2">
                {placeholders.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => insertPlaceholder(p.key)}
                    title={p.label}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-brand-red/10 hover:text-brand-red font-mono transition-colors"
                    data-testid={`bulk-send-placeholder-${p.key.replace(/[<>]/g, '')}`}
                  >
                    {p.key}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="bulk-send-cancel-btn">Abbrechen</Button>
              <Button
                onClick={openContentPreview}
                disabled={previewLoading || !preview?.eligible}
                data-testid="bulk-send-confirm-btn"
              >
                <Eye className="h-4 w-4 mr-1.5" />
                Voransicht ({preview?.eligible ?? 0})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EmailPreviewConfirm
        open={!!mailPreview}
        onClose={() => setMailPreview(null)}
        onConfirm={send}
        confirming={sending}
        title="Sammel-Versand — Voransicht"
        description="Beispieltext mit Platzhaltern. PDF-Anhang wird je Zahler erzeugt. Versand erst nach Bestätigung."
        toLabel={mailPreview?.eligible ? `${mailPreview.eligible} Zahler` : undefined}
        recipients={mailPreview?.recipients}
        subject={mailPreview?.subject}
        html={mailPreview?.html}
        attachmentNote="Anhang: Beitragsaufstellung.pdf (pro Empfänger)"
        sampleNote={mailPreview?.eligible > 1
          ? `Vorlage für ${mailPreview.eligible} Empfänger (Platzhalter werden individuell ersetzt).`
          : null}
        confirmLabel={`An ${mailPreview?.eligible ?? 0} Empfänger senden`}
        testId="bulk-billing-email-preview"
      />
    </>
  );
}
